class PremiumCollectionShowcase extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.viewAllLink = this.querySelector('[data-pcs-view-all]');
    this.featureTrack = this.querySelector('.pcs__features');
    this.featureSlides = Array.from(this.querySelectorAll('.pcs__feature'));
    this.featureMobileQuery = window.matchMedia('(max-width: 767px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.featureIndex = 0;
    this.pendingButton = null;

    this.tablist?.addEventListener('click', this.onClick);
    this.tablist?.addEventListener('keydown', this.onKeydown);
    this.featureTrack?.addEventListener('pointerdown', this.onFeatureInteractionStart);
    this.featureTrack?.addEventListener('pointerup', this.onFeatureInteractionEnd);
    this.featureTrack?.addEventListener('pointercancel', this.onFeatureInteractionEnd);
    this.featureTrack?.addEventListener('scroll', this.onFeatureScroll, { passive: true });
    this.featureMobileQuery.addEventListener('change', this.onFeatureViewportChange);
    this.reducedMotionQuery.addEventListener('change', this.onFeatureViewportChange);
    this.addEventListener('pointerdown', this.onAddButtonPointerDown);
    this.addEventListener('click', this.onAddButtonClick, true);
    this.addEventListener('submit', this.onProductSubmit, true);
    this.addEventListener('click', this.onCardClick);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.featureInView = !('IntersectionObserver' in window);
    if (this.featureTrack && !this.featureInView) {
      this.featureObserver = new IntersectionObserver(([entry]) => {
        this.featureInView = entry.isIntersecting;
        if (this.featureInView) this.startFeatureAutoplay();
        else this.stopFeatureAutoplay();
      }, { rootMargin: '100px 0px' });
      this.featureObserver.observe(this.featureTrack);
    }
    this.startFeatureAutoplay();
    this.reveal();
  }

  disconnectedCallback() {
    this.tablist?.removeEventListener('click', this.onClick);
    this.tablist?.removeEventListener('keydown', this.onKeydown);
    this.featureTrack?.removeEventListener('pointerdown', this.onFeatureInteractionStart);
    this.featureTrack?.removeEventListener('pointerup', this.onFeatureInteractionEnd);
    this.featureTrack?.removeEventListener('pointercancel', this.onFeatureInteractionEnd);
    this.featureTrack?.removeEventListener('scroll', this.onFeatureScroll);
    this.featureMobileQuery?.removeEventListener('change', this.onFeatureViewportChange);
    this.reducedMotionQuery?.removeEventListener('change', this.onFeatureViewportChange);
    this.removeEventListener('pointerdown', this.onAddButtonPointerDown);
    this.removeEventListener('click', this.onAddButtonClick, true);
    this.removeEventListener('submit', this.onProductSubmit, true);
    this.removeEventListener('click', this.onCardClick);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.cartRequest?.abort();
    this.stopFeatureAutoplay();
    clearTimeout(this.featureRestartTimer);
    clearTimeout(this.featureScrollTimer);
    this.observer?.disconnect();
    this.featureObserver?.disconnect();
  }

  onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    this.activate(tab, false);
  };

  onKeydown = (event) => {
    const currentIndex = this.tabs.indexOf(event.target);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.activate(this.tabs[nextIndex], true);
  };

  onBlockSelect = (event) => {
    const tab = event.target.closest?.('[role="tab"]');
    if (tab && this.contains(tab)) this.activate(tab, false);

    const feature = event.target.closest?.('.pcs__feature');
    if (feature && this.featureSlides.includes(feature)) {
      this.featureIndex = this.featureSlides.indexOf(feature);
      feature.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      this.restartFeatureAutoplay();
    }
  };

  onFeatureInteractionStart = () => {
    clearTimeout(this.featureRestartTimer);
    this.stopFeatureAutoplay();
  };

  onFeatureInteractionEnd = () => {
    this.updateFeatureIndex();
    this.restartFeatureAutoplay();
  };

  onFeatureScroll = () => {
    clearTimeout(this.featureScrollTimer);
    this.featureScrollTimer = setTimeout(() => this.updateFeatureIndex(), 100);
  };

  onFeatureViewportChange = () => {
    this.featureIndex = 0;
    if (this.featureTrack) this.featureTrack.scrollLeft = 0;
    this.startFeatureAutoplay();
  };

  onVisibilityChange = () => {
    if (document.hidden) this.stopFeatureAutoplay();
    else this.startFeatureAutoplay();
  };

  startFeatureAutoplay = () => {
    this.stopFeatureAutoplay();
    if (
      !this.featureInView ||
      !this.featureTrack ||
      this.featureSlides.length < 2 ||
      !this.featureMobileQuery.matches ||
      this.reducedMotionQuery.matches ||
      document.hidden
    ) return;

    this.featureAutoplayTimer = setInterval(this.advanceFeature, 2000);
  };

  stopFeatureAutoplay = () => {
    clearInterval(this.featureAutoplayTimer);
    this.featureAutoplayTimer = undefined;
  };

  restartFeatureAutoplay = () => {
    this.stopFeatureAutoplay();
    clearTimeout(this.featureRestartTimer);
    this.featureRestartTimer = setTimeout(this.startFeatureAutoplay, 2000);
  };

  advanceFeature = () => {
    if (!this.featureTrack || this.featureSlides.length < 2) return;
    this.featureIndex = (this.featureIndex + 1) % this.featureSlides.length;
    const firstOffset = this.featureSlides[0].offsetLeft;
    this.featureTrack.scrollTo({
      left: this.featureSlides[this.featureIndex].offsetLeft - firstOffset,
      behavior: 'smooth',
    });
  };

  updateFeatureIndex() {
    if (!this.featureTrack || this.featureSlides.length === 0) return;
    const firstOffset = this.featureSlides[0].offsetLeft;
    const currentPosition = this.featureTrack.scrollLeft + firstOffset;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.featureSlides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - currentPosition);
      if (distance >= closestDistance) return;
      closestDistance = distance;
      this.featureIndex = index;
    });
  }

  onCardClick = (event) => {
    const card = event.target.closest?.('.pcs__card[data-product-url]');
    if (!card || !this.contains(card)) return;
    if (event.target.closest?.('a, button, input, select, textarea, form, product-form-component')) return;
    window.location.assign(card.dataset.productUrl);
  };

  onAddButtonPointerDown = (event) => {
    const button = event.target.closest?.('.pcs__button[type="submit"]');
    if (!button || !this.contains(button)) return;

    const bounds = button.getBoundingClientRect();
    this.animateButtonPress(button, event.clientX - bounds.left, event.clientY - bounds.top);
  };

  onAddButtonClick = (event) => {
    const button = event.target.closest?.('.pcs__button[type="submit"]');
    if (!button || !this.contains(button)) return;

    if (!button.classList.contains('is-clicked')) {
      this.animateButtonPress(button, button.offsetWidth / 2, button.offsetHeight / 2);
    }

    this.pendingButton?.classList.remove('is-loading');
    this.pendingButton?.removeAttribute('aria-busy');
    this.pendingButton = button;
    button.classList.remove('is-success', 'is-error');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    setTimeout(() => {
      if (button.classList.contains('is-loading')) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }, 4000);
  };

  onProductSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

    const button = event.submitter?.matches?.('.pcs__button[type="submit"]')
      ? event.submitter
      : form.querySelector('.pcs__button[type="submit"]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled || button.getAttribute('aria-busy') === 'true' && this.cartRequest) return;
    this.addProductToCart(form, button);
  };

  async addProductToCart(form, button) {
    this.cartRequest?.abort();
    this.cartRequest = new AbortController();
    this.pendingButton = button;
    button.classList.remove('is-success', 'is-error');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      if (window.DesiDanaGheeOffer) {
        await window.DesiDanaGheeOffer.add(new FormData(form));
        this.setButtonSuccess(button);
        await this.openCartDrawer();
        return;
      }
      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
        credentials: 'same-origin',
        signal: this.cartRequest.signal,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this product.');
      }

      this.setButtonSuccess(button);
      await this.openCartDrawer();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.setButtonError(button);
      console.warn('[premium-collection-showcase] Add to cart failed.', error);
    } finally {
      this.cartRequest = null;
    }
  }

  get cartAddUrl() {
    const route = this.dataset.cartAddUrl || '/cart/add';
    return route.endsWith('.js') ? route : `${route}.js`;
  };

  openCartDrawer = async () => {
    const premiumHeader = Array.from(document.querySelectorAll('premium-custom-header')).find((header) =>
      header.querySelector('[data-pch-cart-drawer]')
    );
    if (premiumHeader) {
      const openPremiumDrawer = () => {
        if (typeof premiumHeader.openCartDrawer === 'function') return premiumHeader.openCartDrawer();
        return premiumHeader.querySelector('[data-pch-cart-trigger]')?.click();
      };

      if (!customElements.get('premium-custom-header')) await customElements.whenDefined('premium-custom-header');
      await openPremiumDrawer();
      return;
    }

    const drawer = document.querySelector('theme-drawer#cart-drawer');

    if (!drawer) {
      const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (trigger instanceof HTMLElement) trigger.click();
      return;
    }

    const open = () => {
      if (!drawer.isOpen && typeof drawer.open === 'function') drawer.open();
    };

    if (customElements.get('theme-drawer')) open();
    else customElements.whenDefined('theme-drawer').then(open);
  };

  animateButtonPress(button, x, y) {
    button.style.setProperty('--pcs-ripple-x', `${x}px`);
    button.style.setProperty('--pcs-ripple-y', `${y}px`);
    button.classList.remove('is-clicked');
    void button.offsetWidth;
    button.classList.add('is-clicked');
    setTimeout(() => button.classList.remove('is-clicked'), 720);
  }

  setButtonSuccess(button) {
    if (!button) return;
    button.classList.remove('is-loading', 'is-error');
    button.classList.add('is-success');
    button.removeAttribute('aria-busy');
    if (this.pendingButton === button) this.pendingButton = null;
    setTimeout(() => button.classList.remove('is-success'), 1400);
  }

  setButtonError(button) {
    if (!button) return;
    button.classList.remove('is-loading', 'is-success');
    button.classList.add('is-error');
    button.removeAttribute('aria-busy');
    if (this.pendingButton === button) this.pendingButton = null;
    setTimeout(() => button.classList.remove('is-error'), 900);
  }

  activate(tab, focusTab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    const panelId = tab.getAttribute('aria-controls');
    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const active = panel.id === panelId;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });

    const collectionUrl = tab.dataset.collectionUrl;
    if (this.viewAllLink && collectionUrl) {
      const collectionTitle = tab.dataset.collectionTitle || '';
      this.viewAllLink.href = collectionUrl;
      this.viewAllLink.setAttribute(
        'aria-label',
        collectionTitle ? `${this.viewAllLink.dataset.label} — ${collectionTitle}` : this.viewAllLink.dataset.label
      );
    }

    tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    if (focusTab) tab.focus({ preventScroll: true });
  }

  reveal() {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('is-revealed');
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add('is-revealed');
        this.observer.disconnect();
      },
      { rootMargin: '120px 0px', threshold: 0.08 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('premium-collection-showcase')) {
  customElements.define('premium-collection-showcase', PremiumCollectionShowcase);
}
